import React, { useState, useEffect } from 'react';
import { db, storage, auth } from '../../firebase';
import { ref, onValue, set, push } from 'firebase/database';
import { ref as sRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import InchargeLayout from './InchargeLayout';
import { toast } from 'react-toastify';
import { FaArrowLeft } from 'react-icons/fa';

const UploadDDRPatrak = () => {
    const [societies, setSocieties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingSocieties, setFetchingSocieties] = useState(true);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        societyId: '',
        societyName: '',
        institutionAddress: '',
        outgoingNo: '',
        tipaniDate: '',
        societyType: '',
        fileUrl: ''
    });

    const [file, setFile] = useState(null);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        // Fetch societies for selection
        const societyRef = ref(db, 'ekamwatapzp/SocietyRegistrations');
        const unsubData = onValue(societyRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key]
                })).filter(soc => {
                    const status = (soc.assistantStatus || soc.status || '').toLowerCase();
                    return status === 'approved' || status === 'accepted';
                });
                setSocieties(list);
            }
            setFetchingSocieties(false);
        });

        return () => {
            unsubscribe();
            unsubData();
        };
    }, [navigate]);

    const handleSocietyChange = (e) => {
        const id = e.target.value;
        const selectedSoc = societies.find(s => s.id === id);
        if (selectedSoc) {
            setFormData({
                ...formData,
                societyId: id,
                societyName: selectedSoc.societyName || '',
                institutionAddress: selectedSoc.societyAddress || ''
            });
        } else {
            setFormData({
                ...formData,
                societyId: '',
                societyName: '',
                institutionAddress: ''
            });
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.societyId || !file || !formData.outgoingNo || !formData.tipaniDate || !formData.societyType) {
            toast.error("कृपया सर्व अनिवार्य फील्ड भरा!");
            return;
        }

        setLoading(true);

        try {
            // 1. Upload File
            const storagePath = `ekamwatapzp/DDRPatrak/${Date.now()}_${file.name}`;
            const fileRef = sRef(storage, storagePath);
            await uploadBytes(fileRef, file);
            const downloadURL = await getDownloadURL(fileRef);

            // 2. Save to Database
            const ddrRef = ref(db, 'ekamwatapzp/SocietyDDRPatrak');
            const newDdrRef = push(ddrRef);

            await set(newDdrRef, {
                ...formData,
                fileUrl: downloadURL,
                fileName: file.name,
                submittedAt: Date.now(),
                submittedBy: auth.currentUser.email
            });

            toast.success("डी डी आर पत्रक यशस्वीरित्या अपलोड झाले!");

            // Clear form
            setFormData({
                societyId: '',
                societyName: '',
                institutionAddress: '',
                outgoingNo: '',
                tipaniDate: '',
                societyType: '',
                fileUrl: ''
            });
            setFile(null);

        } catch (error) {
            console.error("Error uploading DDR Patrak:", error);
            toast.error("काहीतरी चूक झाली. कृपया पुन्हा प्रयत्न करा.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <InchargeLayout>
            <div className="min-h-screen bg-gray-50 flex flex-col">
                {/* Header Section */}
                <div className="bg-white border-b border-gray-200 px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2.5 bg-white border border-gray-200 rounded-xl text-gray-400 hover:text-blue-600 hover:border-blue-100 hover:bg-blue-50 transition-all shadow-sm group"
                        >
                            <FaArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                        </button>
                        <h1 className="text-xl font-bold text-gray-800 tracking-tight">Upload Society DDR Patrak</h1>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>Zilla Parishad</span>
                        <span className="text-gray-400">/</span>
                        <span className="font-medium text-gray-600">Upload Society DDR Patrak</span>
                    </div>
                </div>

                <div className="p-4 md:p-8">
                    <div className="max-w-7xl mx-auto bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                        <form onSubmit={handleSubmit} className="p-6 md:p-10 space-y-10">

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                                {/* Select Society */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-bold text-gray-700">
                                        Select Society (सोसायटी निवडा) <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="societyId"
                                        value={formData.societyId}
                                        onChange={handleSocietyChange}
                                        className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                                        required
                                    >
                                        <option value="">Select User</option>
                                        {societies.map(soc => (
                                            <option key={soc.id} value={soc.id}>
                                                {soc.societyName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Institution Address */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-bold text-gray-700">
                                        Institution Address <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="institutionAddress"
                                        value={formData.institutionAddress}
                                        readOnly
                                        placeholder="Institution Address"
                                        className="w-full bg-gray-100 border border-gray-200 rounded-lg px-4 py-3 text-gray-500 outline-none"
                                    />
                                </div>

                                {/* Outgoing No */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-bold text-gray-700">
                                        Outgoing No. (जावक क्रमांक) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="outgoingNo"
                                        value={formData.outgoingNo}
                                        onChange={(e) => setFormData({ ...formData, outgoingNo: e.target.value })}
                                        placeholder="Outgoing Number (जावक क्रमांक)"
                                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                                        required
                                    />
                                </div>

                                {/* Generate Tipani Date */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-bold text-gray-700">
                                        Generate Tipani Date (टिपणी तारीख) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="tipaniDate"
                                        value={formData.tipaniDate}
                                        onChange={(e) => setFormData({ ...formData, tipaniDate: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                                        required
                                    />
                                </div>

                                {/* DDR Patrak File */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-bold text-gray-700">
                                        DDR Patrak (डीडीआर पत्रक) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="file"
                                        onChange={handleFileChange}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 transition-all cursor-pointer"
                                        accept=".pdf"
                                        required
                                    />
                                </div>

                                {/* Society Type */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-bold text-gray-700">
                                        संस्थेचा प्रकार <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="societyType"
                                        value={formData.societyType}
                                        onChange={(e) => setFormData({ ...formData, societyType: e.target.value })}
                                        className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                                        required
                                    >
                                        <option value="">संस्थेचा प्रकार निवडा</option>
                                        <option value="मजूर सहकारी संस्था">शहर</option>
                                        <option value="सुशिक्षित बेरोजगार अभियंता">ग्रामीण</option>
                                    </select>
                                </div>
                            </div>

                            <div className="pt-6">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`px-10 py-2.5 rounded text-white font-medium transition-all shadow-md ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#5c69e5] hover:bg-[#4a58d1] shadow-blue-200 hover:shadow-lg transform active:scale-95'}`}
                                >
                                    {loading ? 'Submitting...' : 'Submit'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </InchargeLayout>
    );
};

export default UploadDDRPatrak;
